import { apiFetch } from './client';

// SlotKind keeps the SPA's allowed values in lockstep with the CHECK
// constraint on members.template_slot.kind. Same vocabulary as
// members.activity.activity_type so a slot's kind propagates 1:1 into
// the activity it stamps. Adding a new kind: update CHECK constraints
// in both migrations + validSlotKind in operations + isValidSlotKind
// in services/templates + validActivityType in operations + this list.
export type SlotKind = 'training' | 'match' | 'meeting' | 'cup' | 'camp' | 'other';
export const SLOT_KINDS: SlotKind[] = ['training', 'match', 'meeting', 'cup', 'camp', 'other'];

export interface TemplateSlot {
  id: string;
  template_id: string;
  day_of_week: number; // 1..7 (Mon..Sun)
  start_time: string;  // "HH:MM"
  end_time: string;
  title: string;
  kind: SlotKind;
  description?: string | null;
  color?: string | null;
  location_id?: string | null;
  zone_id?: string | null;
  team_id: string;
}

export interface Template {
  id: string;
  name: string;
  description?: string | null;
  slots: TemplateSlot[];
}

interface ListResponse {
  templates: Template[];
}

const orgPath = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/templates`;

export async function listTemplates(token: string | null, slug: string): Promise<Template[]> {
  const res = await apiFetch<ListResponse>(orgPath(slug), { token });
  return res.templates;
}

export async function getTemplate(
  token: string | null,
  slug: string,
  id: string,
): Promise<Template> {
  return apiFetch<Template>(`${orgPath(slug)}/${encodeURIComponent(id)}`, { token });
}

export interface TemplateInput {
  name: string;
  description?: string;
}

export async function createTemplate(
  token: string | null,
  slug: string,
  input: TemplateInput,
): Promise<Template> {
  return apiFetch<Template>(orgPath(slug), { method: 'POST', body: input, token });
}

export async function updateTemplate(
  token: string | null,
  slug: string,
  id: string,
  input: TemplateInput,
): Promise<Template> {
  return apiFetch<Template>(`${orgPath(slug)}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    token,
  });
}

export async function deleteTemplate(
  token: string | null,
  slug: string,
  id: string,
  reason = '',
): Promise<void> {
  await apiFetch<void>(`${orgPath(slug)}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: { reason },
    token,
  });
}

export interface SlotInput {
  day_of_week: number;
  start_time: string;
  end_time: string;
  title: string;
  kind?: SlotKind; // omitted = default 'training' on create; preserved on update
  description?: string;
  color?: string;
  location_id?: string | null;
  zone_id?: string | null;
  team_id: string;
}

export async function addSlot(
  token: string | null,
  slug: string,
  templateId: string,
  input: SlotInput,
): Promise<TemplateSlot> {
  return apiFetch<TemplateSlot>(`${orgPath(slug)}/${encodeURIComponent(templateId)}/slots`, {
    method: 'POST',
    body: input,
    token,
  });
}

export async function updateSlot(
  token: string | null,
  slug: string,
  templateId: string,
  slotId: string,
  input: SlotInput,
  propagate: 'none' | 'all' = 'none',
): Promise<TemplateSlot> {
  return apiFetch<TemplateSlot>(
    `${orgPath(slug)}/${encodeURIComponent(templateId)}/slots/${encodeURIComponent(slotId)}?propagate=${propagate}`,
    { method: 'PATCH', body: input, token },
  );
}

export async function deleteSlot(
  token: string | null,
  slug: string,
  templateId: string,
  slotId: string,
  propagate: 'none' | 'all' = 'none',
  reason = '',
): Promise<void> {
  await apiFetch<void>(
    `${orgPath(slug)}/${encodeURIComponent(templateId)}/slots/${encodeURIComponent(slotId)}?propagate=${propagate}`,
    { method: 'DELETE', body: { reason }, token },
  );
}

export interface PlannedActivity {
  slot_id: string;
  week_start: string;
  starts_at: string;
  ends_at: string;
  title: string;
  team_id: string;
}

export interface SlotConflict {
  slot_id: string;
  week_start: string;
  starts_at: string;
  ends_at: string;
  conflicting_activity_ids: string[];
}

export interface PreviewApplyResult {
  planned: PlannedActivity[];
  conflicts: SlotConflict[];
}

export async function previewApplyTemplate(
  token: string | null,
  slug: string,
  templateId: string,
  fromWeekStart: string,
  toWeekStart: string,
  locationIds: string[] = [],
): Promise<PreviewApplyResult> {
  return apiFetch<PreviewApplyResult>(
    `${orgPath(slug)}/${encodeURIComponent(templateId)}/preview-apply`,
    {
      method: 'POST',
      body: {
        from_week_start: fromWeekStart,
        to_week_start: toWeekStart,
        ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
      },
      token,
    },
  );
}

export interface ApplyResult {
  application_id: string;
  activities_count: number;
}

export async function applyTemplate(
  token: string | null,
  slug: string,
  templateId: string,
  fromWeekStart: string,
  toWeekStart: string,
  locationIds: string[] = [],
): Promise<ApplyResult> {
  return apiFetch<ApplyResult>(
    `${orgPath(slug)}/${encodeURIComponent(templateId)}/apply`,
    {
      method: 'POST',
      body: {
        from_week_start: fromWeekStart,
        to_week_start: toWeekStart,
        ...(locationIds.length > 0 ? { location_ids: locationIds } : {}),
      },
      token,
    },
  );
}

// --- AI-generated template draft -------------------------------------

// DraftMode mirrors the backend's templatedraft.Mode* constants.
export type DraftMode = 'new' | 'append';

export interface DraftRequest {
  team_ids: string[];
  mode: DraftMode;
  template_id?: string;
  template_name?: string;
  locale?: string;
  // Optional: narrow the locations the AI is shown. Empty / omitted ⇒ all
  // org locations are fair game.
  location_ids?: string[];
}

export interface DraftSlot {
  day_of_week: number;
  start_time: string;
  end_time: string;
  title: string;
  kind: SlotKind;
  description?: string;
  location_id?: string;
  zone_id?: string;
  team_id: string;
}

export interface DraftTeamSummary {
  team_id: string;
  team_name: string;
  included: boolean;
  skip_reason?: string;
  slot_count: number;
  total_minutes: number;
  slots_with_location: number;
}

export interface DraftWarning {
  kind: string;
  team_id?: string;
  team_name?: string;
  detail: string;
}

export interface DraftDroppedRule {
  team_id: string;
  team_name: string;
  rule: string;
  strictness: string;
  detail: string;
}

export interface TemplateDraft {
  mode: DraftMode;
  template_id?: string;
  template_name?: string;
  slots: DraftSlot[];
  summary: {
    per_team: DraftTeamSummary[];
    warnings: DraftWarning[];
    dropped_rules: DraftDroppedRule[];
  };
  tokens_used: number;
}

// New async API surface — POST returns 202 + job_id, SPA polls GET, then
// flips into the preview step when status='completed'. The old
// `draftTemplate` synchronous function is kept temporarily but unused once
// the SPA finishes migrating; remove in a follow-up PR.

export type DraftJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface DraftJobAccepted {
  job_id: string;
  status: DraftJobStatus;
  created_at: string;
}

export interface DraftJobProgress {
  batches_total: number;
  batches_done: number;
  slots_emitted: number;
}

export interface DraftJobState {
  job_id: string;
  status: DraftJobStatus;
  progress: DraftJobProgress;
  error_message?: string;
  draft?: TemplateDraft;
  created_at: string;
  started_at?: string;
  finished_at?: string;
}

export async function enqueueTemplateDraft(
  token: string | null,
  slug: string,
  body: DraftRequest,
): Promise<DraftJobAccepted> {
  return apiFetch<DraftJobAccepted>(`${orgPath(slug)}/draft`, {
    method: 'POST',
    body,
    token,
  });
}

export async function getTemplateDraftJob(
  token: string | null,
  slug: string,
  jobID: string,
): Promise<DraftJobState> {
  return apiFetch<DraftJobState>(`${orgPath(slug)}/draft/${jobID}`, {
    token,
  });
}

export async function cancelTemplateDraftJob(
  token: string | null,
  slug: string,
  jobID: string,
): Promise<void> {
  await apiFetch<void>(`${orgPath(slug)}/draft/${jobID}/cancel`, {
    method: 'POST',
    token,
  });
}

// Legacy synchronous helper. Now ALSO points at the queued endpoint but
// the old call sites that still treat the response as a draft will fail
// to parse — they should migrate to enqueueTemplateDraft + polling.
export async function draftTemplate(
  token: string | null,
  slug: string,
  body: DraftRequest,
): Promise<TemplateDraft> {
  return apiFetch<TemplateDraft>(`${orgPath(slug)}/draft`, {
    method: 'POST',
    body,
    token,
  });
}
